const { capitalCase } = require('change-case');
const { introspectionApi } = require('da-graphql-client/web');
const { isEnumType } = require('./enum');
const { getOperators, QueryBuilderAttributeTypes } = require('./operators');
const { ignoreAttribute } = require('./ignore');
const { GraphQLFieldKinds, GraphQLScalarTypes, SchemaTypes } = introspectionApi;
const { refineLabel } = require('./label');


/**
 * Utility to translate the given type and its IGraphQLFields to an array of IQueryBuilderAttribute to
 * be used as input for the Query Builder.
 */
const addAttributes = (
    attributes,
    ethosType,
    rootTypeNames,
    relationshipAttribute = null,
    schemaType = SchemaTypes.Filter,
) => {
    if (!attributes || !ethosType || !ethosType.name) {
        return;
    }

    // find the existing attribute(s) that had an object pending for this ethosType so
    // we can add its child fields as attributes.
    const attributesPendingThisType = attributes.filter((a) =>
        a.type === QueryBuilderAttributeTypes.ObjectPending && a.graphQLTypeName === ethosType.name);

    // add its child fields as attributes.
    if (attributesPendingThisType.length > 0) {
        // for each attribute that was waiting on this ethos type to be loaded...
        for (const parentAttribute of attributesPendingThisType) {
            addChildAttributes(attributes, ethosType, rootTypeNames, parentAttribute, null, schemaType);

            // remove the parent from attributes as it's no longer needed
            attributes.splice(attributes.indexOf(parentAttribute), 1);
        }
    } else {
        addChildAttributes(attributes, ethosType, rootTypeNames, null, relationshipAttribute, schemaType);
    }
};

/**
 * Add child attributes to the attribute collection for the given ethosType.
 */
const addChildAttributes = (
    attributes,
    ethosType,
    rootTypeNames,
    parentAttribute = null,
    relationshipAttribute = null,
    schemaType = SchemaTypes.Filter,
) => {
    const fieldsProperty = schemaType === SchemaTypes.Filter ? 'inputFields' : 'fields';
    if (!attributes || !ethosType || !ethosType[fieldsProperty] || !ethosType[fieldsProperty].length) {
        return;
    }

    let ethosFields = ethosType[fieldsProperty];

    // if we have an enum filter type then we really just need to extract the underlying enum
    // type so we don't are about each field.
    const isEnum = isEnumType(ethosType, fieldsProperty);
    if (isEnum) {
        ethosFields = [ethosFields[0]];
    }

    for (const field of ethosFields) {
        if (!field) { continue; }

        const queryBuilderType = getAttributeType(field.type, schemaType);
        if (!queryBuilderType) { continue; }

        const { graphQLTypeName } = queryBuilderType;
        let { type } = queryBuilderType;

        // if we have a root type, turn it into a lookup and don't recurse downward
        if (type === QueryBuilderAttributeTypes.ObjectPending && isFieldALookup(type, graphQLTypeName, rootTypeNames)) {
            if (isLookupFieldExcluded(graphQLTypeName, rootTypeNames)) { // only include max version root type
                continue;
            } else {
                type = QueryBuilderAttributeTypes.Lookup;
            }
        }

        let attribute;
        let pathItem = {
            label: capitalCase(field.name).replace(/\d+$/, ''), // title case and remove trailing numbers
            value: field.name,
        };

        const parentAttributePath = [
            ...(relationshipAttribute ? relationshipAttribute.path || [] : []),
            ...(parentAttribute ? parentAttribute.path || [] : [])
        ];

        let ignore = false;
        if (type === QueryBuilderAttributeTypes.ObjectPending) {
            attribute = {
                graphQLTypeName,
                path: parentAttributePath.length > 0 ? [...parentAttributePath, pathItem] : [pathItem],
                type,
            };
            const evaluationAttribute = {
                ...attribute,
                label: pathItem.label,
                path: parentAttributePath.length > 0 ? [...parentAttributePath] : null,
                value: pathItem.value,
            };
            pathItem.label = refineLabel(evaluationAttribute, true);
            ignore = ignoreAttribute(evaluationAttribute);
        } else {
            // get operators for field type
            const operators = getOperators(type);
            if (!operators.length) {
                continue;
            }
            // if enum, grab the label/value from the parent attribute and use same path
            // as parent attribute
            if (isEnum && parentAttribute) { // enums should have parent attributes
                const path = [...parentAttributePath];
                pathItem = path.pop();
                attribute = {
                    enum: [],
                    graphQLTypeName,
                    label: pathItem.label,
                    operators,
                    path,
                    type,
                    value: pathItem.value,
                };
            } else {
                attribute = {
                    graphQLTypeName,
                    label: pathItem.label,
                    operators,
                    path: parentAttributePath.length > 0 ? [...parentAttributePath] : null,
                    type,
                    value: pathItem.value
                };
            }
            attribute.label = refineLabel(attribute);
            ignore = ignoreAttribute(attribute);
        }

        // ignore certain attributes
        if (ignore) { continue; }

        // set some lookup-specific values
        if (type === QueryBuilderAttributeTypes.Lookup) {
            // we also need to add the a relationship attribute for each lookup attribute
            const childRelationshipAttribute = {
                ...attribute,
                graphQLTypeName,
                type: QueryBuilderAttributeTypes.Relationship
            };

            attributes.push(childRelationshipAttribute);

        } else {
            attributes.push(attribute);

        }

    }
};

/**
 * Return the attribute type for the given field.
 */
const getAttributeType = (fieldType, schemaType = SchemaTypes.Filter) => {
    if (!fieldType) {
        return null;
    }

    if (schemaType === SchemaTypes.Filter && fieldType.kind === GraphQLFieldKinds.InputObject) {
        switch (fieldType.name) {
            case GraphQLScalarTypes.StringFilter:
                return { type: QueryBuilderAttributeTypes.String, graphQLTypeName: fieldType.name };
            case GraphQLScalarTypes.IntFilter:
                return { type: QueryBuilderAttributeTypes.Int, graphQLTypeName: fieldType.name };
            case GraphQLScalarTypes.DateFilter:
                return { type: QueryBuilderAttributeTypes.Date, graphQLTypeName: fieldType.name };
            case GraphQLScalarTypes.IDFilter:
                return null;
        }
        return { type: QueryBuilderAttributeTypes.ObjectPending, graphQLTypeName: fieldType.name };
    }

    if (schemaType === SchemaTypes.Select && (fieldType.kind === GraphQLFieldKinds.Object || fieldType.kind === GraphQLFieldKinds.Scalar)) {
        switch (fieldType.name) {
            case GraphQLScalarTypes.String:
                return { type: QueryBuilderAttributeTypes.String, graphQLTypeName: fieldType.name };
            case GraphQLScalarTypes.Int:
                return { type: QueryBuilderAttributeTypes.Int, graphQLTypeName: fieldType.name };
            case GraphQLScalarTypes.Date:
                return { type: QueryBuilderAttributeTypes.Date, graphQLTypeName: fieldType.name };
            case GraphQLScalarTypes.ID:
                return null;
        }
        return { type: QueryBuilderAttributeTypes.ObjectPending, graphQLTypeName: fieldType.name };
    }

    if (fieldType.kind === GraphQLFieldKinds.Enum) {
        return { type: QueryBuilderAttributeTypes.Enum, graphQLTypeName: fieldType.name };
    }

    if (fieldType.kind === GraphQLFieldKinds.List) {
        return getAttributeType(fieldType.ofType, schemaType);
    }
    return null;
};

/**
 * Return true if field is a lookup, false otherwise.
 */
const isFieldALookup = (attributeType, graphQLTypeName, rootTypeNames) => {
    const rootTypeInfo = rootTypeNames.getRootTypeInfo(graphQLTypeName);
    return attributeType != null && !!rootTypeInfo;
};

/**
 * Return true if lookup field should be excluded because it is not max version, false otherwise.
 */
const isLookupFieldExcluded = (graphQLTypeName, rootTypeNames) => {
    const rootTypeInfo = rootTypeNames.getRootTypeInfo(graphQLTypeName);
    return !!rootTypeInfo && !rootTypeInfo.isMax;
};

/**
 * If there are any original attributes still having ObjectPending, then they need to
 * be removed because the type could not be located.  Otherwise, we recurse forever.
 */
const removeUnresolvedObjectPendingAttributes = (originalAttributes, attributes) => {
    // If there are any original attributes still having ObjectPending, then they need to
    // be removed because the type could not be located.  Otherwise, we recurse forever.
    originalAttributes.forEach((a) => {
        if (a.type === QueryBuilderAttributeTypes.ObjectPending) {
            const index = attributes.indexOf(a);
            if (index >= 0) {
                attributes.splice(index, 1);
            }
        }
    });
};

/**
 * Sort the attributes by label.
 */
const sortAttributes = (attributes) => {
    attributes.sort((a, b) => {
        const aLabel = a.label ? a.label.toLocaleUpperCase() : a.label;
        const bLabel = b.label ? b.label.toLocaleUpperCase() : b.label;
        return aLabel < bLabel ? -1 : aLabel > bLabel ? 1 : 0;
    });
};

const addLookupValueForARelationshipAttribute = (attributes, relationshipAttribute) => {
    const makeRelationshipAttributeALookUp = Object.assign({}, relationshipAttribute);
    makeRelationshipAttributeALookUp.type = QueryBuilderAttributeTypes.Lookup;
    if (makeRelationshipAttributeALookUp.path && makeRelationshipAttributeALookUp.path.length > 0) {
        makeRelationshipAttributeALookUp.path.pop();
    }
    lookupHelper(makeRelationshipAttributeALookUp);
    attributes.unshift(makeRelationshipAttributeALookUp);
    return attributes;
};

const lookupHelper = attribute => {
    attribute.lookupValueLabel = 'Title';
    attribute.lookupValueMinLength = 0;
    return attribute;
};

module.exports = {
    addAttributes,
    addLookupValueForARelationshipAttribute,
    lookupHelper,
    removeUnresolvedObjectPendingAttributes,
    sortAttributes,
};
